/**
 * The saved query something is bound to — a Smart Collection's rule or a pool's
 * feed — loaded if it isn't in memory yet.
 *
 * Saved reports are only fetched by the pages that list them (Reports, the
 * Reports panel). A page that merely USES one as a rule cannot assume they are
 * loaded: after a reload the collection page had no rule in memory, so a Smart
 * Collection rendered as "Not a Smart Collection", resolved to its explicit
 * rows only, and the collection list counted it as empty — until the person
 * happened to open the Reports panel. These hooks fetch the library once when a
 * bound report is missing; the store is reactive, so callers re-render when it
 * arrives.
 */

import { useEffect, useRef } from 'react';
import { useSavedReportsStore } from '../stores/prioritization/savedReportsStore';
import type { SavedReport } from '../types/prioritization/savedReports';

/** Fetch the saved-report library once if any of these bound reports is missing. */
export function useEnsureSavedReports(reportIds: ReadonlyArray<string | null | undefined>): void {
  const reports = useSavedReportsStore((s) => s.reports);
  const fetchReports = useSavedReportsStore((s) => s.fetchFromSupabase);
  const missing = reportIds.some((id) => id && !reports.some((r) => r.id === id));

  // Once per mount: a report still missing after a fetch has been deleted, and
  // re-fetching would only loop.
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (missing && !fetchedRef.current) {
      fetchedRef.current = true;
      void fetchReports();
    }
  }, [missing, fetchReports]);
}

/** One bound report, loaded if missing. */
export function useBoundSavedReport(reportId: string | null | undefined): {
  report: SavedReport | undefined;
  loading: boolean;
} {
  const report = useSavedReportsStore((s) => (reportId ? s.getReportById(reportId) : undefined));
  const loading = useSavedReportsStore((s) => s.isLoading);
  useEnsureSavedReports([reportId]);
  return { report, loading: Boolean(reportId) && !report && loading };
}
